import type {
  EventCategory,
  EventCategoryCreateInput,
  EventCategoryUpdateInput,
} from "@cdt/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { CATEGORIES_QUERY_KEY } from "@/hooks/useCategoriesQuery";
import { ApiError } from "@/lib/api";
import {
  createCategory,
  deleteCategory,
  updateCategory,
} from "@/lib/categoriesApi";

function handleError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    const body = error.body as { error?: string } | null;
    if (body?.error) {
      toast.error(body.error);
      return;
    }
  }
  const message = error instanceof Error ? error.message : String(error);
  toast.error(`${fallback}: ${message}`);
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation<EventCategory, unknown, EventCategoryCreateInput>({
    mutationFn: (payload) => createCategory(payload),
    onSuccess: () => {
      toast.success("Categoria creata");
      qc.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
    onError: (error) => handleError(error, "Creazione fallita"),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation<
    EventCategory,
    unknown,
    { id: string; patch: EventCategoryUpdateInput }
  >({
    mutationFn: ({ id, patch }) => updateCategory(id, patch),
    onSuccess: () => {
      toast.success("Categoria aggiornata");
      qc.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
    onError: (error) => handleError(error, "Aggiornamento fallito"),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation<
    void,
    unknown,
    { id: string; force?: boolean; reassignToId?: string }
  >({
    mutationFn: ({ id, force, reassignToId }) =>
      deleteCategory(id, force, reassignToId),
    onSuccess: () => {
      toast.success("Categoria eliminata");
      qc.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
    onError: (error) => {
      const isUsageConflict =
        error instanceof ApiError &&
        error.status === 409 &&
        (error.body as { usageCount?: number } | null)?.usageCount !== undefined;
      // Usage conflicts are handled by the page (opens the force-delete dialog),
      // no need for a duplicate toast here.
      if (isUsageConflict) return;
      handleError(error, "Eliminazione fallita");
    },
  });
}
